// src/MyAdsList.js
import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import educationData from "../../i18n/education_levels.json"; // Import the education levels JSON
import subjectsData from "../../i18n/general_tutoring_subjects.json"; // Import the subjects JSON
import locationsData from "../../i18n/hong_kong_locations.json";
import typeOptionValue from "../../i18n/type_option_value.json"; // Adjust the path accordingly
import "../List.css"; // Import CSS for styling

const MyAdsList = () => {
  const navigate = useNavigate();

  const { t } = useTranslation(); // Initialize translation
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user, token } = useSelector((state) => state.auth);
  const userId = user?.id; // Safely retrieve user ID

  // Extract subcategories
  const subcategoryList = subjectsData.subjects.flatMap((subject) =>
    subject.subcategories.map((subcategory) => ({
      value: subcategory.value,
      display: subcategory.display[t("language")],
      subject: subject.display[t("language")],
    })),
  );

  const SubjectDisplay = ({ values }) => {
    return (
      <span>
        {JSON.parse(values).map((value) => {
          const subject = subcategoryList.find((sub) => sub.value === value);
          if (subject) {
            return (
              <span className="tag" key={subject.value}>
                {subject.display}
              </span>
            );
          }
          return null;
        })}
      </span>
    );
  };
  // Extract all areas from the locations JSON
  const locationsAreas = locationsData.regions.flatMap((region) =>
    region.areas.map((area) => ({
      value: area.value,
      display: area.display[t("language")], // Change to t('language') for localization
      region: region.display[t("language")], // Include region name for context
    })),
  );
  const LocationDisplay = ({ values }) => {
    return (
      <span>
        {JSON.parse(values).map((value) => {
          const locationEntry = locationsAreas.find(
            (location) => location.value === value,
          );
          return locationEntry ? (
            <span className="tag" key={locationEntry.value}>
              {locationEntry.display}
            </span>
          ) : null;
        })}
      </span>
    );
  };
  // Extract education levels and their grades from the JSON data
  const educationLevels = educationData.flatMap((level) =>
    // Use current language from i18n
    ({
      value: level.value,
      display: level.display[t("language")],
    }),
  );

  const EducationLevelDisplay = ({ values }) => {
    return (
      <span>
        {JSON.parse(values).map((value) => {
          const gradeEntry = educationLevels.find(
            (grade) => grade.value === value,
          );
          return gradeEntry ? (
            <span className="tag" key={gradeEntry.value}>
              {gradeEntry.display}
            </span>
          ) : null;
        })}
      </span>
    );
  };
  const FeeTypeDisplay = ({ feeType }) => {
    const { t } = useTranslation();
    const feeTypesEntry = Object.entries(typeOptionValue.fee_types).find(
      ([key]) => key === feeType,
    );

    return feeTypesEntry ? (
      <span className="fee" key={feeType}>
        {feeTypesEntry[1].display[t("language")]}
      </span>
    ) : null;
  };

  const SubjectTypeDisplay = ({ values }) => {
    const { t } = useTranslation();

    return (
      <>
        {JSON.parse(values).map((value) => {
          const subjectEntry = Object.entries(
            typeOptionValue.subject_types,
          ).find(([key]) => key === value);
          return subjectEntry ? (
            <span className="tag" key={value}>
              {subjectEntry[1].display[t("language")]}
            </span>
          ) : null;
        })}
      </>
    );
  };

  const FormGroupDisplay = ({ values }) => {
    const { t } = useTranslation();

    return (
      <>
        {JSON.parse(values).map((value) => {
          const groupType = Object.entries(
            typeOptionValue.form_group_types,
          ).find(([key]) => key === value);
          return groupType ? (
            <span className="tag" key={value}>
              {groupType[1].display[t("language")]}
            </span>
          ) : null;
        })}
      </>
    );
  };

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/ads.php?userId=${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setAds(response.data.ads);
      } catch (err) {
        setError(
          "獲取廣告時出錯: " + (err.response?.data?.message || err.message),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [userId, token]);

  if (loading) {
    return <p className="loading">載入中...</p>;
  }

  if (error) {
    return <p className="error-message">{error}</p>;
  }
  const handleEdit = (id) => {
    navigate(`/ads/${id}`); // Navigate to the 'create-ad' page
    console.log(`Editing ad with id: ${id}`);
  };
  return (
    <div className="page-container">
      <div className="title">{t("myAds")}</div>
      {!Array.isArray(ads) ? (
        <div className="create-ad-button">
          <div className="ads-list">沒有找到您的廣告。</div>
          <button className=" " onClick={() => navigate("/create-ad")}>
            創建廣告
          </button>
        </div>
      ) : (
        <ul className="">
          {Array.isArray(ads) &&
            ads.map((ad) => (
              <li
                className="card   hover:border-orange-300  cursor-pointer"
                key={ad.id}
                onClick={() => handleEdit(ad.id)}
              >
                <div className="header-bar grid grid-cols-1 md:grid-cols-4  gap-1">
                  <div className={ad.adType}>{t(ad.adType)}</div>
                  <div>
                    <FormGroupDisplay values={ad.formGroupType} />
                  </div>
                  <div>
                    <SubjectTypeDisplay values={ad.subjectType} />
                  </div>{" "}
                  <div className="">
                    <span className="ad-fee">
                      {t("ad.fee")}: ${ad.fee} /
                      <FeeTypeDisplay feeType={ad.feeType} />
                    </span>
                  </div>
                </div>
                <div className="ads-item-content grid grid-cols-1 md:grid-cols-2 gap-1 ">
                  <div className="ads-item">
                    <p className="">
                      <strong>{t("ad.subject")}:</strong>
                      <span>
                        <SubjectDisplay values={ad.subject} />
                      </span>
                    </p>
                    <p className="">
                      <strong>{t("ad.gradeLevel")}:</strong>
                      <EducationLevelDisplay values={ad.gradeLevel} />
                    </p>
                    <p className="">
                      <strong>{t("ad.locations")}:</strong>
                      <LocationDisplay values={ad.location} />
                    </p>
                    <p className="">
                      <strong>{t("ad.expirationDate")}:</strong>{" "}
                      {new Date(ad.expirationDate).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="ads-item">
                    {ad.requirements && (
                      <p className="line-clamp-4">
                        <strong>{t("ad.requirements")}:</strong>{" "}
                        {ad.requirements}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

export default MyAdsList;
